import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock, Check, ArrowRight, ArrowLeft } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { useSettings } from '../context/SettingsContext';
import { getServices, createBooking, getTimeSlots, getBlockedDates } from '../services/api';
import { toast } from 'sonner';

const Book = () => {
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get('service');
  const { businessInfo } = useSettings();

  const [services, setServices] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(preselectedService || '');
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesData, slotsData, blockedData] = await Promise.all([
          getServices(),
          getTimeSlots(true),
          getBlockedDates()
        ]);
        setServices(servicesData);
        setTimeSlots(slotsData.map(s => s.time_value));
        setBlockedDates(blockedData.map(b => new Date(b.date + 'T00:00:00')));
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load booking data. Please refresh the page.');
      } finally {
        setLoadingServices(false);
      }
    };
    fetchData();
  }, []);

  // Build disabled days array including blocked dates
  const disabledDays = [
    { before: new Date() },
    { dayOfWeek: [0] },
    ...blockedDates
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const selectedServiceData = services.find(s => s.id === parseInt(selectedService));
    
    const bookingData = {
      client_name: formData.name,
      client_email: formData.email,
      client_phone: formData.phone,
      service_id: selectedServiceData.id,
      service_name: selectedServiceData.name,
      service_price: selectedServiceData.price,
      service_duration: selectedServiceData.duration,
      booking_date: selectedDate?.toISOString().split('T')[0],
      booking_time: selectedTime,
      notes: formData.notes || null
    };

    try {
      const result = await createBooking(bookingData);
      setBookingResult(result);
      setIsComplete(true);
      toast.success('Booking request submitted!');
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedServiceData = services.find(s => s.id === parseInt(selectedService));
  const canProceedStep1 = selectedService !== '';
  const canProceedStep2 = selectedDate && selectedTime;
  const canSubmit = formData.name && formData.email && formData.phone;

  if (isComplete && bookingResult) {
    return (
      <main className="section-padding">
        <div className="container">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[var(--accent-blush)] rounded-full">
              <Check size={28} className="text-[var(--text-heading)]" />
            </div>
            <h1 className="heading-1 mb-3">Booking Received!</h1>
            <p className="body-regular text-[var(--text-secondary)] mb-6">
              Thank you, {formData.name}! We will confirm your booking within 24 hours.
            </p>
            <div className="p-5 bg-[var(--bg-secondary)] rounded-xl mb-6 text-left">
              <h3 className="heading-3 mb-4">Booking Details</h3>
              <div className="space-y-2 body-small">
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Service</span>
                  <span className="font-medium">{bookingResult.service_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Date</span>
                  <span>{new Date(bookingResult.booking_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Time</span>
                  <span>{bookingResult.booking_time}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-[var(--border-light)]">
                  <span className="text-[var(--text-secondary)]">Total</span>
                  <span className="font-medium">${bookingResult.service_price}</span>
                </div>
              </div>
            </div>
            <a
              href={`https://wa.me/${businessInfo.whatsapp.replace(/\D/g, '')}?text=Hi! I just booked ${bookingResult.service_name}.`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary w-full justify-center"
            >
              Message on WhatsApp
            </a>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="text-center max-w-xl mx-auto">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">Appointments</p>
            <h1 className="hero-medium mb-4">Book Your Appointment</h1>
            <p className="body-large text-[var(--text-secondary)]">
              Select your service, choose a date and time, and we will confirm your booking.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-10">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <button
                    onClick={() => s < step && setStep(s)}
                    disabled={s > step}
                    className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-all ${
                      step >= s
                        ? 'bg-[var(--text-primary)] text-[var(--bg-primary)]'
                        : 'bg-[var(--bg-subtle)] text-[var(--text-light)]'
                    } ${s < step ? 'cursor-pointer' : ''}`}
                  >
                    {step > s ? <Check size={16} /> : s}
                  </button>
                  {s < 3 && (
                    <div className={`w-12 sm:w-16 h-0.5 rounded-full ${
                      step > s ? 'bg-[var(--text-primary)]' : 'bg-[var(--bg-subtle)]'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {step === 1 && (
              <div>
                <h2 className="heading-2 mb-6 text-center">Choose a Service</h2>
                {loadingServices ? (
                  <div className="grid gap-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="p-5 bg-[var(--bg-card)] rounded-xl skeleton h-24" />
                    ))}
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {services.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => setSelectedService(String(service.id))}
                        className={`p-5 text-left rounded-xl transition-all ${
                          selectedService === String(service.id)
                            ? 'bg-[var(--accent-blush)] ring-2 ring-[var(--accent-blush-dark)]'
                            : 'bg-[var(--bg-card)] hover:bg-[var(--bg-subtle)]'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <h3 className="heading-3">{service.name}</h3>
                          <span className="text-lg font-medium" style={{fontFamily: "'Cormorant Garamond', serif"}}>${service.price}</span>
                        </div>
                        <div className="flex items-center gap-3 text-[var(--text-light)]">
                          <Clock size={14} />
                          <span className="body-small">{service.duration}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-8 flex justify-end">
                  <button
                    onClick={() => setStep(2)}
                    disabled={!canProceedStep1}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="heading-2 mb-6 text-center">Choose Date and Time</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-[var(--bg-card)] p-5 rounded-xl">
                    <h3 className="heading-3 mb-4">Select Date</h3>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={disabledDays}
                      className="mx-auto"
                    />
                  </div>

                  <div className="bg-[var(--bg-card)] p-5 rounded-xl">
                    <h3 className="heading-3 mb-4">Select Time</h3>
                    {selectedDate ? (
                      <div className="grid grid-cols-2 gap-2">
                        {timeSlots.map((time) => (
                          <button
                            key={time}
                            onClick={() => setSelectedTime(time)}
                            className={`py-3 px-4 text-sm rounded-lg transition-all ${
                              selectedTime === time
                                ? 'bg-[var(--text-primary)] text-[var(--bg-primary)]'
                                : 'bg-[var(--bg-subtle)] hover:bg-[var(--bg-secondary)]'
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="body-regular text-[var(--text-secondary)] text-center py-8">
                        Please select a date first
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-8 flex justify-between">
                  <button onClick={() => setStep(1)} className="btn-text">
                    <ArrowLeft size={16} />
                    Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!canProceedStep2}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="heading-2 mb-6 text-center">Your Details</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block body-small font-medium mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none transition-colors"
                        placeholder="Jane Doe"
                      />
                    </div>
                    <div>
                      <label className="block body-small font-medium mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none transition-colors"
                        placeholder="(519) 555-0123"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block body-small font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none transition-colors"
                      placeholder="jane@email.com"
                    />
                  </div>
                  <div>
                    <label className="block body-small font-medium mb-2">Notes (optional)</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none transition-colors resize-none"
                      placeholder="Any special requests..."
                    />
                  </div>

                  <div className="p-5 bg-[var(--bg-secondary)] rounded-xl">
                    <h3 className="heading-3 mb-3">Summary</h3>
                    <div className="space-y-2 body-small">
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">Service</span>
                        <span>{selectedServiceData?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">Date</span>
                        <span>{selectedDate?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">Time</span>
                        <span>{selectedTime}</span>
                      </div>
                      <div className="flex justify-between pt-2 mt-2 border-t border-[var(--border-light)] font-medium">
                        <span>Total</span>
                        <span>${selectedServiceData?.price}{selectedServiceData?.price_note ? ` ${selectedServiceData.price_note}` : ''}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4">
                    <button type="button" onClick={() => setStep(2)} className="btn-text">
                      <ArrowLeft size={16} />
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={!canSubmit || isSubmitting}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Booking...' : 'Confirm Booking'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Book;
